import React from 'react';
import {
  Alert,
  Box,
  Typography,
  Button
} from '@mui/material';
import dayjs from 'dayjs';

/**
 * TimelineIssuesAlert Component
 * 
 * Displays warnings for roles missing timeline data and provides auto-fix functionality.
 * Extracted from StaffingStep for better maintainability.
 */
const TimelineIssuesAlert = ({
  rolesWithoutTimeline,
  formData,
  onFixTimeline,
  onDismiss,
  readOnly = false
}) => {
  if (!rolesWithoutTimeline || rolesWithoutTimeline.length === 0) {
    return null;
  }

  const handleFixAllTimelines = () => {
    rolesWithoutTimeline.forEach(issue => {
      const milestone = formData?.milestones?.find(m => m.id === issue.milestoneId);
      const role = milestone?.roles?.find(r => r.id === issue.roleId);
      
      if (milestone && role) {
        const milestoneStart = dayjs(milestone.start_date);
        const milestoneEnd = dayjs(milestone.end_date);
        const milestoneStartMonth = milestoneStart.month() + 1;
        const milestoneEndMonth = milestoneEnd.month() + 1;
        
        const milestoneDefaultRanges = [{
          startMonth: milestoneStartMonth,
          endMonth: milestoneEndMonth,
          allocation: role.allocation || 100,
          timelineVersion: '3.0'
        }];
        
        // Call the parent's role change handler
        onFixTimeline(milestone.id, role.id, 'dateRanges', milestoneDefaultRanges);
        
        const duration = Math.max(1, milestoneEndMonth - milestoneStartMonth + 1);
        onFixTimeline(milestone.id, role.id, 'overlap_months', duration);
        onFixTimeline(milestone.id, role.id, 'timelineVersion', '3.0');
      }
    });
    
    // Clear the issues after fixing
    onDismiss();
  };

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          ⚠️ Timeline Issues Detected ({rolesWithoutTimeline.length} roles)
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The following roles are missing timeline information:
        </Typography>
        <Box sx={{ ml: 2 }}>
          {rolesWithoutTimeline.map((issue, index) => (
            <Typography key={index} variant="body2" sx={{ fontSize: '0.85rem' }}>
              • <strong>{issue.roleName}</strong> in {issue.milestoneName}
            </Typography>
          ))}
        </Box>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleFixAllTimelines}
            disabled={readOnly}
          >
            Set to Milestone Dates
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={onDismiss}
          >
            Dismiss Warning
          </Button>
        </Box>
      </Box>
    </Alert>
  );
};

export default TimelineIssuesAlert;